import Vue from 'vue'
import {
  STATUS_PAGE_EVENT_FEED_URL,
  STATUS_PAGE_NOTICE_DISMISSED_KEY,
  STATUS_PAGE_NOTICE_POLL_INTERVAL_MS,
  SYSTEM_STATUS_REQUEST_TIMEOUT_MS,
} from '@/constant/systemStatus.constant'

const MAX_DISMISSED_IDS = 20
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
// The feed's end date is "Nov 11, 2026 07:00" in GMT; Date.parse on that shape differs across browsers.
const FEED_DATE_PATTERN = /^([A-Z][a-z]{2}) (\d{1,2}), (\d{4}) (\d{1,2}):(\d{2})$/

const readDismissedIds = () => {
  try {
    const ids = JSON.parse(localStorage.getItem(STATUS_PAGE_NOTICE_DISMISSED_KEY))
    return Array.isArray(ids) ? ids.map(String) : []
  } catch (error) {
    return []
  }
}

export const statusPageNoticeState = Vue.observable({ notice: null, dismissedIds: [] })

let pollTimer = null
let isFetching = false

const parseFeedDateGmt = (value) => {
  const match = FEED_DATE_PATTERN.exec(String(value || '').trim())
  if (!match) return null
  const [, monthName, day, year, hours, minutes] = match
  const month = MONTHS.indexOf(monthName)
  if (month < 0) return null
  return Date.UTC(Number(year), month, Number(day), Number(hours), Number(minutes))
}

const isActiveAnnouncement = (event, now) => {
  if (!event || (event.type || event.eventType) !== 'announcement') return false
  if (!event.id || !(event.title || event.content)) return false
  const startsAt = Number(event.timestamp) * 1000
  if (!Number.isFinite(startsAt) || startsAt > now) return false
  if (!event.endDateGMT) return true
  const endsAt = parseFeedDateGmt(event.endDateGMT)
  return endsAt !== null && endsAt > now
}

export const pickActiveNotice = (feed, now = Date.now()) => {
  const events = feed && Array.isArray(feed.results) ? feed.results : []
  const latest = events
    .filter((event) => isActiveAnnouncement(event, now))
    .sort((a, b) => Number(b.timestamp) - Number(a.timestamp))[0]
  if (!latest) return null
  return {
    id: String(latest.id),
    title: String(latest.title || ''),
    text: String(latest.content || latest.description || ''),
  }
}

const fetchFeed = async () => {
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), SYSTEM_STATUS_REQUEST_TIMEOUT_MS)
  try {
    const response = await fetch(STATUS_PAGE_EVENT_FEED_URL, { credentials: 'omit', signal: controller.signal })
    if (!response.ok) return null
    return await response.json()
  } finally {
    clearTimeout(timer)
  }
}

const refreshNotice = async () => {
  if (isFetching) return
  isFetching = true
  try {
    const feed = await fetchFeed()
    if (feed) statusPageNoticeState.notice = pickActiveNotice(feed)
  } catch (error) {
    // A status-page outage must never raise a bar of its own, so the last known notice stays.
  } finally {
    isFetching = false
  }
}

const stopStatusPageNotice = () => {
  clearInterval(pollTimer)
  pollTimer = null
  statusPageNoticeState.notice = null
}

export const startStatusPageNotice = (isEnabled) => {
  if (pollTimer) return
  statusPageNoticeState.dismissedIds = readDismissedIds()
  pollTimer = setInterval(() => {
    if (isEnabled()) refreshNotice()
    else stopStatusPageNotice()
  }, STATUS_PAGE_NOTICE_POLL_INTERVAL_MS)
  refreshNotice()
}

export const isStatusPageNoticeDismissed = (id) => statusPageNoticeState.dismissedIds.includes(String(id))

export const dismissStatusPageNotice = (id) => {
  const ids = [...statusPageNoticeState.dismissedIds.filter((dismissedId) => dismissedId !== String(id)), String(id)]
    .slice(-MAX_DISMISSED_IDS)
  statusPageNoticeState.dismissedIds = ids
  try {
    localStorage.setItem(STATUS_PAGE_NOTICE_DISMISSED_KEY, JSON.stringify(ids))
  } catch (error) {
    // Storage can be full or blocked; the dismissal still holds for this page load.
  }
}

export const buildStatusPageAnnouncement = ({ isSystemStatusActive, defaultMessage }) => {
  const { notice } = statusPageNoticeState
  if (isSystemStatusActive) {
    return {
      criticalLevel: 3,
      statusPage: {
        key: 'system-status',
        title: notice ? notice.title : '',
        text: notice ? notice.text : defaultMessage,
        noticeId: null,
      },
    }
  }
  if (!notice || isStatusPageNoticeDismissed(notice.id)) return null
  return {
    criticalLevel: 2,
    statusPage: {
      key: `status-page-notice-${notice.id}`,
      title: notice.title,
      text: notice.text,
      noticeId: notice.id,
    },
  }
}
